import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, orderBy, query } from 'firebase/firestore';
import styles from './Details.module.css'; 
import headStyles from './CountryHead.module.css';
import FooterPage from '../Components/Footer';

const NewsDetail = ({ lang = 'EN' }) => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [activeImg, setActiveImg] = useState('');
  const [moreNews, setMoreNews] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchNews = async () => {
      const docRef = doc(db, "news", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setNews({
          title: lang === 'EN' ? (data.titleEn || "AP URDU") : (data.titleUr || "اے پی اردو"),
          content: lang === 'EN' ? (data.descEn || "AP URDU News") : (data.descUr || "اے پی اردو خبریں"),
          images: data.images || [],
          videos: data.videos || []
        });
        setActiveImg(data.images?.[0] || 'https://apurdu.org/logo.png');
      }
      const q = query(collection(db, "news"), orderBy("timestamp", "desc"));
      const querySnapshot = await getDocs(q);
      const allNews = querySnapshot.docs
        .filter(doc => doc.id !== id)
        .map(doc => ({ id: doc.id, ...doc.data() }));
      setMoreNews(allNews);
    };
    fetchNews();
  }, [id, lang]);

  // Helper function to format Firestore timestamp cleanly
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return dateObj.toLocaleDateString(lang === 'EN' ? 'en-US' : 'ur-PK', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return '';
    }
  };

  if (!news) return <h2 style={{ textAlign: 'center', marginTop: '100px' }}>Loading...</h2>;

  return (
    <>
      <Helmet>
        <title>{news.title} | AP URDU</title>
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://apurdu.org/news/${id}`} />
        <meta property="og:title" content={news.title} />
        <meta property="og:description" content={news.content.substring(0, 150)} />
        <meta property="og:image" content={activeImg} />
        <meta property="fb:app_id" content="1348047287452812" />
        <link rel="canonical" href={`https://apurdu.org/news/${id}`} />
      </Helmet>

      <div className={styles.container} dir={lang === 'UR' ? 'rtl' : 'ltr'}>
        <h1 className={styles.mainTitle}>{news.title}</h1>
        <div className={styles.mainLayout}>
          <div className={styles.leftSide}>
            <div className={styles.bigMediaContainer}>
              <img src={activeImg} className={styles.bigImage} alt="Main" />
            </div>
            {news.images?.length > 1 && (
              <div className={styles.thumbnailGallery}>
                {news.images.map((img, i) => (
                  <img key={i} src={img} onClick={() => setActiveImg(img)} className={activeImg === img ? styles.activeThumb : ''} alt="thumb" />
                ))}
              </div>
            )}
            <div className={styles.articleDetails}>
              <p className={styles.paragraph}>{news.content}</p>
            </div>
          </div>
          <div className={styles.rightSide}>
            <h3>{lang === 'EN' ? "Videos" : "ویڈیوز"}</h3>
            {news.videos?.length > 0 ? (
              news.videos.map((vid, i) => <video key={i} src={vid} controls className={styles.sideVideo} />)
            ) : <p>{lang === 'EN' ? "No videos" : "کوئی ویڈیو نہیں"}</p>}
          </div>
        </div>
        
        {/* More News Section with Dates Added */}
        <div className={styles.moreNewsSection}>
          <h2>{lang === 'EN' ? "More News" : "مزید خبریں"}</h2>
          <div className={styles.moreNewsGrid}>
            {moreNews.map(item => (
              <Link to={`/news/${item.id}`} key={item.id} className={styles.moreNewsCard}>
                <div style={{ position: 'relative' }}>
                  <img src={item.randomHeroImage || item.images?.[0]} alt="news" />
                </div>
                <div className={styles.moreNewsCardContent}>
                  <h4>{lang === 'EN' ? item.titleEn : item.titleUr}</h4>
                  {item.timestamp && (
                    <span className={styles.newsDate} style={{ fontSize: '12px', color: '#6c757d', marginTop: '6px', display: 'block' }}>
                      {formatDate(item.timestamp)}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Country Head Section */}
      <div className={headStyles.countryHeadSection}>
        <div className={headStyles.countryHeadContainer}>
          
          <div className={headStyles.leftSide}>
            <img 
              src="https://apurdu.org/logo.png" 
              alt="Logo" 
              className={headStyles.countryHeadLogo} 
            />
            <div className={headStyles.countryHeadInfo}>
              <h3>Siddique Anzar</h3>
              <h1>Country Head Pakistan</h1>
            </div>
          </div>

          <div className={headStyles.rightSide}>
            <img 
              src="/CEO.jpeg" 
              alt="Siddique Anzar" 
              className={headStyles.countryHeadPic} 
            />
          </div>

        </div>
      </div>

      <FooterPage lang={lang} />
    </>
  );
};

export default NewsDetail;